/* eslint-disable @typescript-eslint/no-empty-function */
import { User } from "firebase/auth";
import {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
} from "react";
import { useLocation } from "react-router-dom";
import { auth, db } from "../firebase";
import styles from "../assets/styles/loading.module.scss";
import { doc, onSnapshot } from "firebase/firestore";

const AuthContext = createContext<{
  user: User | null;
  setUser: (user: User | null) => void;
  isSubscribed: boolean;
  setIsSubscribed: (isSubscribed: boolean) => void;
}>({
  user: null,
  setUser: () => {},
  isSubscribed: false,
  setIsSubscribed: () => {},
});

export function useAuthContext() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children?: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const location = useLocation();

  const value = {
    user,
    loading,
    setUser,
    isSubscribed,
    setIsSubscribed,
  };

  useEffect(() => {
    const unsubscribed = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setUser(user);

        const userDocRef = doc(db, "users", user.uid);
        const unsubFirestore = onSnapshot(
          userDocRef,
          (docSnap) => {
            if (docSnap.exists()) {
              const data = docSnap.data();
              const status = data.subscription?.status;
              setIsSubscribed(status === "active");
            } else {
              console.warn("User doc not found in Firestore");
              setIsSubscribed(false);
            }
            setLoading(false);
          },
          (error) => {
            console.error("Failed to subscribe to Firestore:", error);
            setIsSubscribed(false);
            setLoading(false);
          },
        );

        // ログアウト時などにFirestore監視を解除
        return () => {
          unsubFirestore();
        };
      } else {
        setUser(null);
        setIsSubscribed(false);
      }
      setLoading(false);
    });

    return () => {
      unsubscribed();
    };
  }, []);

  // FIXME: この処理が何なのか分からないが危険そう
  if (
    loading &&
    location.pathname !== "/about" &&
    !/^\/contents\/[A-Za-z0-9-]+\/detail$/.test(location.pathname) &&
    !/^\/interview\/[A-Za-z0-9-]+$/.test(location.pathname) &&
    !/^\/contents\/[A-Za-z0-9-]+\/detail\/present$/.test(location.pathname) &&
    location.pathname !== "/terms" &&
    location.pathname !== "/tokutei" &&
    location.pathname !== "/privacy"
  ) {
    return (
      <div className={styles.loading}>
        <p>Loading...</p>
      </div>
    );
  } else {
    return (
      <AuthContext.Provider value={value}>
        {!loading && children}
      </AuthContext.Provider>
    );
  }
}
